import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  ListProps,
  Typography
} from '@mui/material'
import { Add, ExpandMore } from '@mui/icons-material'
import { Asset, AssetGroup, Aurora, Device, Generator, Project } from '../../types'
import GeneratorDetailsList from '../GeneratorDetailsList'
import AuroraDetailsList from '../AuroraDetailsList'
import AssetGroupDetailsList from '../AssetGroupDetailsList'
import AuroraBlockDisplay from '../AuroraBlockDisplay'
import { AssetGroupType } from '../../enums'

export interface InstallationCompletedStepProps {
  project: Project
  device?: Device
  generator?: Generator
  aurora?: Aurora
  asset?: Asset
  assetGroup?: AssetGroup
}

export default function InstallationCompletedStep({
  project,
  device,
  generator,
  aurora,
  asset,
  assetGroup
}: InstallationCompletedStepProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  return (
    <Card>
      <CardHeader title={t('installationCompleted')} />
      <CardContent sx={{ pt: 0, pb: 2 }}>
        <Alert severity="success">{t('installSuccess')}</Alert>

        <Grid container>
          {aurora && (
            <Grid item xs={12}>
              <AuroraBlockDisplay aurora={aurora} sx={{ my: 2 }} />
            </Grid>
          )}

          {asset && (
            <Grid item xs={12}>
              <Accordion sx={{ boxShadow: 0, width: '100%' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={500}>{t('assetDetails')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <AssetDetailsList asset={asset} disablePadding />
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {assetGroup && (
            <Grid item xs={12}>
              <Accordion sx={{ boxShadow: 0, width: '100%' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={500}>{t('assetGroupDetails')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <AssetGroupDetailsList
                    assetGroup={assetGroup}
                    disablePadding
                    withTimestamps={false}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {generator && (
            <Grid item xs={12}>
              <Accordion sx={{ boxShadow: 0, width: '100%' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={500}>{t('generatorDetails')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <GeneratorDetailsList
                    generator={generator}
                    disablePadding
                    withSecondaryActions={false}
                    withTimestamps={false}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {aurora && (
            <Grid item xs={12}>
              <Accordion sx={{ boxShadow: 0, width: '100%' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={500}>{t('auroraDetails')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <AuroraDetailsList aurora={aurora} disablePadding />
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </CardContent>

      {assetGroup && (
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          {assetGroup.type === AssetGroupType.AURORA && (
            <Button
              variant="text"
              color="primary"
              component={NavLink}
              endIcon={<Add />}
              to={`/install/aurora?projectId=${project.id}&assetGroupId=${assetGroup.id}&skipSelectProject=true`}
            >
              {t('installNew')}
            </Button>
          )}

          <Button
            variant="contained"
            color="success"
            component={NavLink}
            to={`/auroras/group/${assetGroup.id}`}
          >
            {t('done')}
          </Button>
        </CardActions>
      )}

      {device && (
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="success"
            component={NavLink}
            to={`/devices/${device.name}`}
          >
            {t('done')}
          </Button>
        </CardActions>
      )}
    </Card>
  )
}

interface AssetDetailsListProps extends Omit<ListProps, 'children'> {
  asset: Asset
}

function AssetDetailsList({ asset, ...props }: AssetDetailsListProps) {
  const { t } = useTranslation('installation')

  return (
    <List {...props}>
      {asset?.name && (
        <ListItemButton key="asset.name">
          <ListItemText primary={asset.name} secondary={t('asset:name')} />
        </ListItemButton>
      )}

      {asset?.company?.name && (
        <ListItemButton key="asset.company.name">
          <ListItemText primary={asset.company.name} secondary={t('asset:company')} />
        </ListItemButton>
      )}

      {asset?.project?.name && (
        <ListItemButton key="asset.project.name">
          <ListItemText primary={asset.project.name} secondary={t('asset:project')} />
        </ListItemButton>
      )}

      {asset?.level?.name && (
        <ListItemButton key="asset.level.name">
          <ListItemText primary={asset.level.name} secondary={t('asset:level')} />
        </ListItemButton>
      )}

      {asset?.lift?.name && (
        <ListItemButton key="asset.lift.name">
          <ListItemText primary={asset.lift.name} secondary={t('asset:lift')} />
        </ListItemButton>
      )}

      {asset?.device?.name && (
        <ListItemButton key="asset.device.name">
          <ListItemText primary={asset.device.name} secondary={t('device:name')} />
        </ListItemButton>
      )}

      {asset?.device?.shortUUID && (
        <ListItemButton key="asset.device.shortUUID">
          <ListItemText primary={asset.device.shortUUID} secondary={t('device:shortUUID')} />
        </ListItemButton>
      )}

      {asset?.device?.firmwareVersion && (
        <ListItemButton key="asset.device.firmwareVersion">
          <ListItemText
            primary={asset.device.firmwareVersion}
            secondary={t('device:firmwareVersion')}
          />
        </ListItemButton>
      )}

      {asset?.device?.hardwareVersion && (
        <ListItemButton key="asset.device.hardwareVersion">
          <ListItemText
            primary={asset.device.hardwareVersion}
            secondary={t('device:hardwareVersion')}
          />
        </ListItemButton>
      )}
    </List>
  )
}
